import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './ImageLightbox.css';
import { API_URL } from '../config';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.3;

function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}

/* ─── Lightbox Modal ─────────────────────────────────── */
function LightboxModal({ imageUrl, prompt, onClose }) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [closing, setClosing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const dragStart = useRef(null);
  const panAtDragStart = useRef({ x: 0, y: 0 });
  const lastPinchDist = useRef(null);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, 180);
  }, [onClose]);

  // Reset pan when zoom resets to 1
  const resetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const zoomIn = useCallback(() => {
    setZoom(z => clamp(parseFloat((z + ZOOM_STEP).toFixed(2)), MIN_ZOOM, MAX_ZOOM));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom(z => {
      const next = clamp(parseFloat((z - ZOOM_STEP).toFixed(2)), MIN_ZOOM, MAX_ZOOM);
      if (next <= 1) setPan({ x: 0, y: 0 });
      return next;
    });
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === '=' || e.key === '+') zoomIn();
      if (e.key === '-') zoomOut();
      if (e.key === '0') resetView();
      if (e.key === 'ArrowLeft')  setPan(p => ({ ...p, x: p.x + 40 }));
      if (e.key === 'ArrowRight') setPan(p => ({ ...p, x: p.x - 40 }));
      if (e.key === 'ArrowUp')    setPan(p => ({ ...p, y: p.y + 40 }));
      if (e.key === 'ArrowDown')  setPan(p => ({ ...p, y: p.y - 40 }));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose, zoomIn, zoomOut, resetView]);

  // Scroll to zoom
  const onWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
    setZoom(z => {
      const next = clamp(parseFloat((z + delta).toFixed(2)), MIN_ZOOM, MAX_ZOOM);
      if (next <= 1) setPan({ x: 0, y: 0 });
      return next;
    });
  }, []);

  // Mouse drag
  const onMouseDown = useCallback((e) => {
    if (zoom <= 1) return;
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
    panAtDragStart.current = pan;
  }, [zoom, pan]);

  const onMouseMove = useCallback((e) => {
    if (!dragging) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setPan({ x: panAtDragStart.current.x + dx, y: panAtDragStart.current.y + dy });
  }, [dragging]);

  const onMouseUp = useCallback(() => setDragging(false), []);

  // Touch events
  const onTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastPinchDist.current = Math.hypot(dx, dy);
    } else if (e.touches.length === 1 && zoom > 1) {
      setDragging(true);
      dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      panAtDragStart.current = pan;
    }
  }, [zoom, pan]);

  const onTouchMove = useCallback((e) => {
    e.preventDefault();
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      if (lastPinchDist.current) {
        const ratio = dist / lastPinchDist.current;
        setZoom(z => clamp(parseFloat((z * ratio).toFixed(2)), MIN_ZOOM, MAX_ZOOM));
      }
      lastPinchDist.current = dist;
    } else if (e.touches.length === 1 && dragging) {
      const dx = e.touches[0].clientX - dragStart.current.x;
      const dy = e.touches[0].clientY - dragStart.current.y;
      setPan({ x: panAtDragStart.current.x + dx, y: panAtDragStart.current.y + dy });
    }
  }, [dragging]);

  const onTouchEnd = useCallback(() => {
    setDragging(false);
    lastPinchDist.current = null;
  }, []);

  // Double-click to reset
  const onDblClick = useCallback(() => resetView(), [resetView]);

  // Download
  const handleDownload = useCallback(async () => {
    if (!imageUrl || isDownloading) return;
    setIsDownloading(true);
    try {
      const proxyUrl = `${API_URL}/proxy-image?url=${encodeURIComponent(imageUrl)}`;
      let blob;
      try {
        const res = await fetch(proxyUrl);
        if (!res.ok) throw new Error('proxy');
        blob = await res.blob();
      } catch {
        const res = await fetch(imageUrl);
        blob = await res.blob();
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const slug = (prompt || 'fashionai-design').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
      a.href = url;
      a.download = `${slug}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      window.open(imageUrl, '_blank');
    } finally {
      setIsDownloading(false);
    }
  }, [imageUrl, prompt, isDownloading]);

  const transform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;
  const zoomPct = Math.round(zoom * 100);

  return createPortal(
    <div
      className={`lightbox-overlay${closing ? ' closing' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <button className="lb-close-x" onClick={handleClose} aria-label="Close">✕</button>

      {/* Canvas */}
      <div
        className={`lightbox-canvas${zoom > 1 ? ' zoomed' : ''}`}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onDoubleClick={onDblClick}
      >
        <div className="lightbox-img-wrap" style={{ transform }}>
          <img src={imageUrl} alt={prompt || 'Generated design'} draggable={false} />
        </div>
        <p className="lb-hint">
          Scroll to zoom · Drag to pan · Double-click to reset<br />
          <kbd>+</kbd>/<kbd>−</kbd> zoom · Arrow keys pan · <kbd>Esc</kbd> close
        </p>
      </div>

      {/* Controls bar */}
      <div className="lightbox-controls">
        <div className="lightbox-prompt">
          Prompt: <span>"{prompt || 'No prompt'}"</span>
        </div>
        <div className="lightbox-actions">
          <div className="lb-zoom-bar">
            <button className="lb-zoom-btn" onClick={zoomOut} disabled={zoom <= MIN_ZOOM} aria-label="Zoom out">−</button>
            <span className="lb-zoom-label">{zoomPct}%</span>
            <button className="lb-zoom-btn" onClick={zoomIn} disabled={zoom >= MAX_ZOOM} aria-label="Zoom in">+</button>
          </div>
          <button className="lb-reset-btn" onClick={resetView}>Reset</button>
          <button className="lb-download-btn" onClick={handleDownload} disabled={isDownloading}>
            {isDownloading ? '⏳' : '⬇️'} Download
          </button>
          <button className="lb-close-btn" onClick={handleClose}>✕ Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ─── Trigger Button ──────────────────────────────────── */
export default function ImageLightbox({ imageUrl, prompt }) {
  const [open, setOpen] = useState(false);

  if (!imageUrl) return null;

  return (
    <>
      <button
        className="lightbox-trigger-btn"
        onClick={() => setOpen(true)}
        title="View fullscreen (scroll to zoom, drag to pan)"
        aria-label="Open fullscreen viewer"
      >
        🔍 Fullscreen
      </button>
      {open && (
        <LightboxModal
          imageUrl={imageUrl}
          prompt={prompt}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
